import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'shared/components/common/modal'
import { t, Trans } from 'src/translations'
import Image from 'shared/components/common/Image'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { useCart } from 'public/components/online_ordering/CartContext'
import {
  CheckoutFormData,
  useCheckout
} from 'public/components/online_ordering/CheckoutContext'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { getDisplayBusinessName } from 'shared/js/locationUtils'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'
import {
  CreditIcon,
  DollarIcon,
  ExpandLessIcon,
  ExpandMoreIcon,
  LocationIcon,
  ScheduleIcon,
  ToastCashIcon
} from '@toasttab/buffet-pui-icons'
import { formatDeliveryAddress } from 'public/components/online_ordering/addressUtils'
import { useCustomer } from 'src/public/components/online_ordering/CustomerContextCommon'
import {
  PaymentOption,
  usePayment
} from 'src/public/components/online_ordering/PaymentContext'
import { ToastPaymentType } from 'public/components/online_ordering/types'
import { useGiftCard } from 'public/components/online_ordering/GiftCardContext'
import isTomorrow from 'date-fns/isTomorrow'
import isToday from 'date-fns/isToday'
import { toLocalTime } from 'shared/js/timeUtils'
import { flattenModifiers } from 'public/components/default_template/online_ordering/order/OrderItemsUtil'
import { useSubmitOrder } from 'public/components/default_template/online_ordering/checkout/useSubmitOrder'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'
import useTracker from 'src/lib/js/hooks/useTracker'
import classnames from 'classnames'
import { useOOLocations } from 'public/components/online_ordering/FulfillmentContext'
import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import { getCartQuantity } from 'public/components/default_template/online_ordering/cart/cartUtils'

const TIMER_IN_SECONDS = 10

const ReviewOrderModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const { ooRestaurant } = useRestaurant()
  const { cart, cartGuid } = useCart()
  const { orderError, setOrderError } = useCheckout()
  const { getValues } = useFormContext<CheckoutFormData>()
  const [isPlacingOrder, setIsPlacingOrder] = useState(false)
  const isMobile = useIsMobile(ScreenWidth.EXTRA_SMALL)
  const [secondsRemaining, setSecondsRemaining] = useState(TIMER_IN_SECONDS)
  const submittedRef = useRef(false)
  const submitOrder = useSubmitOrder()
  const tracker = useTracker()
  const ooLocations = useOOLocations()
  const multiLocation = ooLocations.length > 1
  const isDeliveryOrder = useMemo(
    () => cart?.diningOptionBehavior === DiningOptionBehavior.Delivery,
    [cart?.diningOptionBehavior]
  )

  useEffect(() => {
    if (isOpen) tracker.track(TrackingEvents.REVIEW_ORDER_SCREEN_VIEWED)
  }, [isOpen, tracker])

  const resetTimer = useCallback(
    (restart?: boolean) => {
      setSecondsRemaining(TIMER_IN_SECONDS)
      const button = document.getElementById('review-order-looks-good-button')
      button?.getAnimations().forEach((anim) => {
        anim.cancel()
        if (restart) {
          anim.play()
        }
      })
      if (restart) {
        tracker.track(TrackingEvents.TIMER_RESET, { secondsRemaining })
      }
    },
    [tracker, secondsRemaining]
  )

  const handleClose = useCallback(() => {
    resetTimer()
    submittedRef.current = false
    // Animate slide out
    const element = document.getElementsByClassName('reviewOrderModal')[0]
    if (element && isMobile) {
      element.classList.add('exiting')
      setTimeout(() => {
        element?.classList.remove('exiting')
        onClose()
      }, 200)
    } else {
      onClose()
    }
  }, [onClose, isMobile, resetTimer])

  // Close on error
  useEffect(() => {
    if (orderError) handleClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderError])

  const onConfirmPlaceOrder = useCallback(
    async (activeClick: boolean) => {
      if (!cartGuid || isPlacingOrder || submittedRef.current) return // guard against double-order placement
      setIsPlacingOrder(true)
      setOrderError(null)
      tracker.track(TrackingEvents.PLACED_ORDER_AFTER_REVIEW, {
        activeClick,
        secondsRemaining
      })
      try {
        document
          .getElementById('review-order-looks-good-button')
          ?.getAnimations()[0]
          ?.finish()
        await submitOrder(getValues())
        submittedRef.current = true
        // On success, handleCompletedOrder is invoked by CheckoutContext and navigates away
      } catch (error) {
        setOrderError(error)
      } finally {
        setIsPlacingOrder(false)
      }
    },
    [
      cartGuid,
      isPlacingOrder,
      setOrderError,
      getValues,
      submitOrder,
      secondsRemaining,
      tracker
    ]
  )

  useEffect(() => {
    if (!isOpen) return
    if (secondsRemaining <= 0) {
      onConfirmPlaceOrder(false)
      return
    }

    // Count down
    const timerId = setInterval(
      () => setSecondsRemaining((prev) => prev - 1),
      1000
    )

    return () => clearInterval(timerId)
  }, [isOpen, secondsRemaining, onConfirmPlaceOrder])

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      testId='review-order-modal'
      preventOverlayClose
    >
      <ModalOverlay fadeIn={false} color='rgba(0, 0, 0, 0.65)' />
      <ModalContent
        contentClassName='modalContentV2 bottomDrawerMobile reviewOrderModal'
        ariaLabelledBy='review-order-modal-title'
        ariaDescribedBy='review-order-modal-body'
      >
        <div className='modalBody' id='review-order-modal-body'>
          <div className='section reviewHeader'>
            <div
              className='headerText'
              id='review-order-modal-title'
              tabIndex={-1}
            >
              {t(
                `review-order-modal.placing-${
                  isDeliveryOrder ? 'delivery' : 'pickup'
                }-order`
              )}
            </div>
            <div className='headerDescription'>
              {t('review-order-modal.review-your-order')}
            </div>
            {multiLocation && ooRestaurant && !isDeliveryOrder && (
              <div className='multiLocationWarning'>
                <div className='warningText'>
                  {t('review-order-modal.we-have-multiple-locations')}
                </div>
                <div className='addressAndButtonWrapper'>
                  <div className='locationAddress'>
                    {ooRestaurant.location.address1}
                  </div>
                  <button
                    className='goBackButton'
                    onClick={() => {
                      tracker.track(TrackingEvents.GO_BACK_CLICKED, {
                        secondsRemaining,
                        source: 'MUO Warning'
                      })
                      handleClose()
                    }}
                  >
                    {t('review-order-modal.go-back')}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className='separator' />
          <OrderDetailsSummaries />
          <div className='separator' />
          <OrderItemsSummary resetTimer={() => resetTimer(true)} />
        </div>
        <div className='section footer'>
          <button
            type='button'
            onClick={() => onConfirmPlaceOrder(true)}
            disabled={isPlacingOrder}
            id='review-order-looks-good-button'
            data-testid='review-order-looks-good-button'
            className={classnames('placeOrderButton', {
              placingOrder: isPlacingOrder
            })}
            aria-live='off'
            aria-label={t('review-order-modal.place-order-aria-label')}
          >
            {isPlacingOrder
              ? t('review-order-modal.placing-order')
              : t('review-order-modal.looks-good', {
                  secondsRemaining: secondsRemaining.toString().padStart(2, '0')
                })}
          </button>
          <button
            type='button'
            className='cancelButton'
            onClick={() => {
              tracker.track(TrackingEvents.GO_BACK_CLICKED, {
                secondsRemaining,
                source: 'Footer CTA'
              })
              handleClose()
            }}
            disabled={isPlacingOrder}
          >
            {t('review-order-modal.go-back')}
          </button>
        </div>
      </ModalContent>
    </Modal>
  )
}

const OrderDetailsSummaries = () => {
  const { ooRestaurant, selectedLocation } = useRestaurant()
  const { cart } = useCart()
  const { customer } = useCustomer()
  const isDelivery = useMemo(
    () => cart?.diningOptionBehavior === DiningOptionBehavior.Delivery,
    [cart?.diningOptionBehavior]
  )
  const savedAddressLabel = useMemo(() => {
    const deliveryInfo = cart?.order?.deliveryInfo
    if (isDelivery && deliveryInfo) {
      return customer?.addresses.find(
        (address) =>
          address.deliveryInfo.address1 === deliveryInfo.address1 &&
          (!Boolean(address.deliveryInfo.address2 && deliveryInfo.address2) || // "" vs. null
            address.deliveryInfo.address2 === deliveryInfo.address2) &&
          address.deliveryInfo.city === deliveryInfo.city &&
          address.deliveryInfo.zipCode === deliveryInfo.zipCode
      )?.name
    } else {
      return undefined
    }
  }, [cart?.order?.deliveryInfo, customer?.addresses, isDelivery])

  if (!ooRestaurant) return null

  return (
    <div className='section orderDetailsSummaries'>
      <div className='orderDetailsSummary'>
        <ScheduleIcon accessibility='decorative' />
        <FulfillmentTimeSummary />
      </div>
      {isDelivery ? (
        <div
          className='orderDetailsSummary'
          data-testid='delivery-details-summary'
        >
          <LocationIcon accessibility='decorative' />
          <div className='orderDetails'>
            {savedAddressLabel && (
              <>
                <span className='bold'>{savedAddressLabel}</span>
                <span className='separator bold'>·</span>
              </>
            )}
            <span>
              {formatDeliveryAddress(cart?.order?.deliveryInfo, { full: true })}
            </span>
          </div>
        </div>
      ) : (
        <div
          className='orderDetailsSummary'
          data-testid='pickup-details-summary'
        >
          <Image src='/icons/walking.svg' alt='' />
          <div className='orderDetails'>
            <span className='bold'>
              {getDisplayBusinessName(
                ooRestaurant.name,
                selectedLocation?.name
              )}
            </span>
            <span className='separator bold'>·</span>
            <span>{ooRestaurant.location.address1}</span>
          </div>
        </div>
      )}
      <div className='orderDetailsSummary'>
        {/* eslint-disable-next-line @toasttab/buffet/icon-accessibility */}
        <PaymentDetailsIcon />
        <PaymentDetailsSummary />
      </div>
    </div>
  )
}

const FulfillmentTimeSummary = () => {
  const { cart } = useCart()
  if (!cart) return null
  const isDelivery = cart.diningOptionBehavior === DiningOptionBehavior.Delivery
  // This indicates an ASAP order
  if (!cart.fulfillmentDateTime) {
    const minutes =
      (isDelivery ? cart.deliveryQuoteTime : cart.takeoutQuoteTime) ??
      cart.quoteTime
    if (!minutes) {
      return (
        <span data-testid='fulfillment-time-summary'>
          {isDelivery
            ? t('review-order-modal.asap-delivery')
            : t('review-order-modal.asap-pickup')}
        </span>
      )
    }
    const now = new Date()
    const time = toLocalTime(
      new Date(now.setMinutes(now.getMinutes() + minutes)).toISOString()
    )
    return (
      <span data-testid='fulfillment-time-summary'>
        <Trans
          i18nKey={`review-order-modal.estimated-${
            isDelivery ? 'arrival' : 'pickup'
          }-today`}
          components={{ span: <span className='bold' /> }}
          values={{ time }}
        />
      </span>
    )
  }
  const fulfillmentTime = new Date(cart.fulfillmentDateTime)
  const itIsTomorrow = isTomorrow(fulfillmentTime)
  const itIsToday = isToday(fulfillmentTime)
  const formattedTime = toLocalTime(cart.fulfillmentDateTime)

  if (itIsToday || itIsTomorrow) {
    return (
      <span data-testid='fulfillment-time-summary'>
        <Trans
          i18nKey={`review-order-modal.estimated-${
            isDelivery ? 'arrival' : 'pickup'
          }-${itIsToday ? 'today' : 'tomorrow'}`}
          components={{ span: <span className='bold' /> }}
          values={{ time: formattedTime }}
        />
      </span>
    )
  }

  const formattedDate = fulfillmentTime.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  })

  return (
    <span data-testid='fulfillment-time-summary'>
      <Trans
        i18nKey={`review-order-modal.estimated-${
          isDelivery ? 'arrival' : 'pickup'
        }-later`}
        components={{ span: <span className='bold' /> }}
        values={{ date: formattedDate, time: formattedTime }}
      />
    </span>
  )
}

const PaymentDetailsIcon = () => {
  const { toastCashAppliedAmount, orderTotal } = useCheckout()
  const { paymentType, paymentOption } = usePayment()

  if (paymentOption === PaymentOption.UponReceipt) {
    return <DollarIcon accessibility='decorative' />
  } else if (paymentType === ToastPaymentType.APPLE_PAY) {
    return (
      <Image alt='' src='/icons/apple-pay.svg' data-testid='apple-pay-icon' />
    )
  } else if (
    toastCashAppliedAmount > 0 &&
    toastCashAppliedAmount === orderTotal
  ) {
    return <ToastCashIcon accessibility='decorative' />
  } else {
    return <CreditIcon accessibility='decorative' />
  }
}

const PaymentDetailsSummary = () => {
  const { toastCashAppliedAmount, giftCardAppliedAmount, orderTotal } =
    useCheckout()
  const { paymentType, paymentOption, cardDetails, tipAmount } = usePayment()
  const { giftCardNumber } = useGiftCard()
  const gcLast4 = useMemo(() => giftCardNumber.slice(-4), [giftCardNumber])

  return (
    <div
      className='orderDetails paymentDetails'
      data-testid='payment-details-summary'
    >
      {paymentOption === PaymentOption.UponReceipt && (
        <div className='paymentMethod'>
          {t('review-order-modal.pay-at-restaurant')}
        </div>
      )}
      {giftCardAppliedAmount > 0 && (
        <div className='paymentMethod'>
          <span>{t('review-order-modal.gift-card')}</span>
          {gcLast4 && (
            <div>
              <span>****</span>
              <span className='bold'>{gcLast4}</span>
            </div>
          )}
        </div>
      )}
      {toastCashAppliedAmount > 0 && (
        <div className='paymentMethod'>
          {t('review-order-modal.toast-cash')}
        </div>
      )}
      {paymentType === ToastPaymentType.APPLE_PAY && (
        <div className='paymentMethod'>{t('review-order-modal.apple-pay')}</div>
      )}
      {cardDetails &&
        paymentOption === PaymentOption.PayNow &&
        orderTotal > 0 && (
          <div className='paymentMethod'>
            {cardDetails.brand !== 'UNKNOWN' && (
              <span>{cardDetails.brand}</span>
            )}
            <div>
              <span>****</span>
              <span className='bold'>{cardDetails.lastFour}</span>
            </div>
          </div>
        )}
      {paymentOption === PaymentOption.PayNow &&
        orderTotal === 0 &&
        tipAmount === 0 && (
          <div className='paymentMethod'>
            {t('review-order-modal.no-payment-required')}
          </div>
        )}
    </div>
  )
}

const OrderItemsSummary = ({ resetTimer }: { resetTimer: () => void }) => {
  const { cart } = useCart()
  const quantities = cart?.order?.selections?.map((selection) => {
    return selection?.quantity ?? 1
  })
  const highestQuantity = quantities ? Math.max(...quantities) : 1
  const [expanded, setExpanded] = useState(false)
  const expandedRef = useRef(false)
  const numItems = getCartQuantity(cart)

  return (
    <div className='section orderItemsSummary'>
      <button
        className='orderItemsSummaryHeader'
        onClick={() => {
          if (!expanded && !expandedRef.current) {
            expandedRef.current = true
            resetTimer()
          }
          setExpanded(!expanded)
        }}
        aria-expanded={expanded}
      >
        <h2>{t('review-order-modal.order-summary', { numItems })}</h2>
        <div className='expandButton'>
          {expanded ? (
            <ExpandLessIcon
              aria-label={t('review-order-modal.order-summary.collapse')}
            />
          ) : (
            <ExpandMoreIcon
              aria-label={t('review-order-modal.order-summary.expand')}
            />
          )}
        </div>
      </button>
      <AnimatedSection expanded={expanded}>
        <div className='summary'>
          {cart?.order?.selections?.map((selection) => (
            <div className='selection' key={selection?.guid}>
              <div
                className={classnames('selectionQuantity', {
                  threeDigits: highestQuantity >= 100,
                  twoDigits: highestQuantity >= 10 && highestQuantity < 100
                })}
              >
                {selection?.quantity}
              </div>
              <div className='selectionDetails'>
                <div className='selectionName'>{selection?.name}</div>
                {selection?.modifiers &&
                  flattenModifiers(selection.modifiers)?.map((modifier) => (
                    <div key={modifier.name} className='modifier'>
                      {modifier.name}
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </AnimatedSection>
    </div>
  )
}

export default ReviewOrderModal
